/**
 * Iter-229 — Consolidated multi-day Bookings (V2).
 *
 * PRESENTATION-LAYER ONLY: groups shifts by (client, event_title, role, venue)
 * into a single consolidated card. Each day-shift retains its own check-in/
 * check-out/recap/photo/reimbursement workflow. NO database changes.
 *
 * Original Bookings.js stays untouched at /talent/bookings; V2 is at
 * /talent/bookings-v2.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { talentAPI } from '../../utils/api';
import { isFlatPay, formatPay } from '../../utils/payFormat';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Search, Filter, ChevronDown, ChevronRight, MapPin, Calendar,
  Play, MoreVertical, CalendarPlus, Loader2, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { MessageOpsButton } from '../../components/talent/MessageOpsButton';
import { parseLocalDate, formatDate, formatLongDate } from '../../utils/timeFormat';
import BookingActionsSheet from '../../components/booking/BookingActionsSheet';

/* ------------------------------------------------------------- grouping --- */

function groupKey(b) {
  // Stable key — must NOT use shift_id (that would un-group). Identical
  // event_title + role + client + venue + date-window-7-days = a series.
  //
  // Iter-231b — when event_title is NULL on one shift in a series (real
  // scenario: Vivian's Gift Show had a null event_title on Jun 12 only),
  // the key would change and break consolidation. Fall back to venue_name
  // so all shifts in the same series collapse correctly.
  const client = b.client_id || b.client_name || 'unknown';
  const eventLabel = (b.event_title || b.venue_name || b.gig_title || '').trim().toLowerCase();
  const role = (b.role_id || b.gig_role_name || b.role_name || '').toString().trim().toLowerCase();
  const venue = (b.venue_name || b.city || '').trim().toLowerCase();
  return `${client}|${eventLabel}|${role}|${venue}`;
}

function groupBookings(bookings) {
  const groups = new Map();
  for (const b of bookings) {
    const k = groupKey(b);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(b);
  }
  const result = [];
  for (const [k, arr] of groups) {
    arr.sort((a, b) => (a.shift_date || '').localeCompare(b.shift_date || ''));
    // Iter-231b — pick the FIRST non-empty event_title across the whole
    // series rather than just arr[0].event_title, so a null on one day
    // doesn't downgrade the consolidated card label to "Event".
    const named = arr.find(x => (x.event_title || '').trim());
    const first = arr[0];
    result.push({
      key: k,
      client_name: first.client_name || first.client_company || 'Client',
      event_title: (named?.event_title || first.event_title || first.venue_name || 'Event'),
      role: first.gig_role_name || first.role_name || '',
      venue: first.venue_name || '',
      city: first.city || '',
      state: first.state || '',
      hourly_rate: first.hourly_rate || first.pay_rate || null,
      pay_type: first.pay_type || 'hourly',
      flat_pay: first.flat_pay != null ? first.flat_pay : null,
      payout_terms: first.payout_terms || null,
      shifts: arr,
      total_shifts: arr.length,
      first_date: arr[0].shift_date,
      last_date: arr[arr.length - 1].shift_date,
      is_multi_day: arr.length > 1,
    });
  }
  // Sort groups: today first, then upcoming by first_date, completed last
  const today = new Date().toISOString().slice(0, 10);
  result.sort((a, b) => {
    const aDone = (a.last_date || '') < today;
    const bDone = (b.last_date || '') < today;
    if (aDone !== bDone) return aDone ? 1 : -1;
    return (a.first_date || '').localeCompare(b.first_date || '');
  });
  return result;
}

/* ------------------------------------------------------------ formatters --- */

function dateRange(first, last) {
  if (!first) return '';
  if (!last || first === last) return formatLongDate(first);
  const f = parseLocalDate(first);
  const l = parseLocalDate(last);
  if (!f || !l) return `${formatDate(first)} – ${formatDate(last)}`;
  const sameMonth = f.getMonth() === l.getMonth() && f.getFullYear() === l.getFullYear();
  const fStr = f.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const lStr = sameMonth
    ? l.getDate().toString()
    : l.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fStr}–${lStr}, ${l.getFullYear()}`;
}

function shiftDayLabel(s, idx) {
  const d = parseLocalDate(s.shift_date);
  if (!d) return `Day ${idx + 1}`;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function shiftStatusMeta(s) {
  const today = new Date().toISOString().slice(0, 10);
  const isToday = s.shift_date === today;
  const isPast = (s.shift_date || '') < today;
  if (s.status === 'completed' || (isPast && s.clock_out_time)) {
    return { label: 'COMPLETED', tone: 'emerald', icon: '✓' };
  }
  if (s.status === 'checked_in' || s.status === 'in_progress') {
    return { label: 'CLOCKED IN', tone: 'cyan', icon: '●' };
  }
  if (isToday) return { label: 'TODAY', tone: 'cyan', icon: '◆' };
  if (isPast) return { label: 'MISSED', tone: 'rose', icon: '!' };
  return { label: 'UPCOMING', tone: 'slate', icon: '○' };
}

/* ----------------------------------------------------------- components --- */

const Pill = ({ tone = 'slate', children, className = '' }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-${tone}-50 text-${tone}-700 border-${tone}-200 ${className}`}>
    {children}
  </span>
);

const ClientBadge = ({ name }) => {
  const initials = (name || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
  return (
    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-700 grid place-items-center text-white font-bold text-lg shadow shrink-0">
      {initials || 'IV'}
    </div>
  );
};

function ConsolidatedCard({ group, expanded, onToggle, onOpenActions, onShiftClick }) {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const todayShift = group.shifts.find(s => s.shift_date === today);
  const nextShift = group.shifts.find(s => (s.shift_date || '') >= today
    && !['completed', 'cancelled'].includes(s.status));
  const activeShift = todayShift || nextShift || group.shifts[group.shifts.length - 1];
  const todayIdx = activeShift ? group.shifts.indexOf(activeShift) : -1;

  const isInProgress = activeShift && ['checked_in', 'in_progress'].includes(activeShift.status);
  const isToday = activeShift && activeShift.shift_date === today;

  return (
    <Card
      className="overflow-hidden border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
      data-testid={`booking-card-${group.key}`}
      onClick={(e) => {
        if (e.target.closest('button,a,[role="button"]')) return;
        const targetId = activeShift?.id || group.shifts?.[0]?.id || group.key;
        if (targetId) navigate(`/talent/booking/${targetId}`);
      }}
    >
      <CardContent className="p-0">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <ClientBadge name={group.client_name} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                {isToday && <Pill tone="emerald">● TODAY</Pill>}
                <Pill tone="cyan">Confirmed</Pill>
                {group.is_multi_day && <Pill tone="slate">{group.total_shifts}-day event</Pill>}
              </div>
              <h4 className="font-semibold text-slate-900 leading-tight" data-testid={`booking-title-${group.key}`}>
                {group.event_title}
              </h4>
              <p className="text-sm text-slate-500 truncate">
                {group.role}{group.role && (group.venue || group.city) ? ' · ' : ''}{group.venue || group.city}{group.state ? `, ${group.state}` : ''}
              </p>
            </div>
            <button
              className="p-1.5 rounded-lg hover:bg-slate-100"
              onClick={() => onOpenActions(group, activeShift)}
              title="Actions"
              data-testid={`actions-btn-${group.key}`}
            >
              <MoreVertical className="w-4 h-4 text-slate-500" />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-100 text-sm">
            <div>
              <div className="text-[10px] uppercase font-semibold text-slate-400">Dates</div>
              <div className="font-semibold text-slate-900">{dateRange(group.first_date, group.last_date)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-semibold text-slate-400">Shifts</div>
              <div className="font-semibold text-slate-900">{group.total_shifts} scheduled</div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-semibold text-slate-400">Pay</div>
              <div className="font-semibold text-slate-900">{isFlatPay(group) ? formatPay(group) : (group.hourly_rate ? `$${group.hourly_rate}/hr` : '—')}</div>
            </div>
          </div>

          {group.payout_terms && (
            <p className="mt-2 text-[11px] text-emerald-700" data-testid="booking-payout-terms">
              Payout: within {group.payout_terms.business_days} business days after event completion
              {group.payout_terms.expedited ? ` · ${group.payout_terms.expedited_label}` : ''}
            </p>
          )}

          <div className="flex gap-2 mt-4">
            {isToday && (
              <Button
                onClick={() => navigate(`/talent/clock?booking=${activeShift.id}`)}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid={`primary-cta-${group.key}`}
              >
                <Play className="w-3.5 h-3.5 mr-1.5" />
                {isInProgress ? 'Clock Out · Day ' + (todayIdx + 1) : 'Check In · Day ' + (todayIdx + 1)}
              </Button>
            )}
            {!isToday && (
              <Button
                variant="outline"
                onClick={() => onShiftClick(group, activeShift)}
                className="flex-1"
                data-testid={`details-btn-${group.key}`}
              >
                View details
              </Button>
            )}
            {group.is_multi_day && (
              <Button
                variant="outline"
                onClick={() => onToggle(group.key)}
                className="px-3"
                data-testid={`expand-btn-${group.key}`}
              >
                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="ml-1.5">{expanded ? 'Collapse' : 'Expand'}</span>
              </Button>
            )}
          </div>

          {/* Iter-417 — reachable "Message iEvntür" for every booking (was
              previously only on the clock-in card of a same-day gig). */}
          {activeShift?.id && activeShift?.shift_id && (
            <div className="mt-2">
              <MessageOpsButton
                booking={{ id: activeShift.id, shift_id: activeShift.shift_id }}
                className="w-full justify-center"
              />
            </div>
          )}
        </div>

        {expanded && group.is_multi_day && (
          <ol className="divide-y divide-slate-100 border-t border-slate-200 bg-slate-50/60" data-testid={`expanded-shifts-${group.key}`}>
            {group.shifts.map((s, i) => {
              const m = shiftStatusMeta(s);
              const isHighlight = s.shift_date === today;
              return (
                <li
                  key={s.id}
                  className={`flex items-center gap-3 p-4 ${isHighlight ? 'bg-cyan-50/80 border-l-4 border-cyan-500' : ''}`}
                  data-testid={`expanded-shift-${s.id}`}
                >
                  <span className={`flex items-center justify-center w-9 h-9 rounded-full bg-${m.tone}-${m.tone === 'slate' ? '200' : '500'} text-${m.tone === 'slate' ? 'slate-500' : 'white'} shadow shrink-0 font-bold`}>
                    {m.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900">Day {i + 1} · {shiftDayLabel(s, i)}{isHighlight ? ' · TODAY' : ''}</div>
                    <div className="text-xs text-slate-500">
                      {s.start_time && s.end_time ? `${s.start_time} – ${s.end_time}` : 'Time TBD'}
                      {s.hours_worked ? ` · ${s.hours_worked}h logged` : ''}
                    </div>
                  </div>
                  {isHighlight ? (
                    <Button
                      size="sm"
                      onClick={() => navigate(`/talent/clock?booking=${s.id}`)}
                      className="bg-cyan-600 hover:bg-cyan-700 text-white"
                      data-testid={`expanded-checkin-${s.id}`}
                    >
                      Check In →
                    </Button>
                  ) : (
                    <span className={`text-[11px] font-semibold text-${m.tone}-700`}>{m.label}</span>
                  )}
                </li>
              );
            })}
            <li className="p-3 bg-slate-50 flex justify-center">
              <Button variant="ghost" size="sm" className="text-slate-700">
                <CalendarPlus className="w-3.5 h-3.5 mr-1.5" /> Add series to calendar
              </Button>
            </li>
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------- page --- */

const BookingsV2 = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [actionTarget, setActionTarget] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let mounted = true;
    talentAPI.getBookings()
      .then(r => { if (mounted) setBookings(r.data || []); })
      .catch(() => toast.error('Failed to load bookings'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const groups = useMemo(() => {
    const g = groupBookings(bookings);
    if (!query.trim()) return g;
    const q = query.trim().toLowerCase();
    return g.filter(grp =>
      (grp.event_title || '').toLowerCase().includes(q) ||
      (grp.client_name || '').toLowerCase().includes(q) ||
      (grp.role || '').toLowerCase().includes(q) ||
      (grp.venue || '').toLowerCase().includes(q) ||
      (grp.city || '').toLowerCase().includes(q)
    );
  }, [bookings, query]);

  const toggle = (k) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const openActions = (group, shift) => setActionTarget({ group, shift });
  const onShiftClick = (group, shift) => {
    if (shift?.id) navigate(`/talent/booking/${shift.id}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="bookings-v2-page">
      <div className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">My Bookings</h1>
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-cyan-600" /> New consolidated view · multi-day events grouped
              <button onClick={() => navigate('/talent/bookings?classic=1')} className="ml-2 text-cyan-700 hover:underline" data-testid="switch-to-legacy">
                Switch to classic
              </button>
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button className="p-2 rounded-lg hover:bg-slate-100"><Search className="w-4 h-4 text-slate-500" /></button>
            <button className="p-2 rounded-lg hover:bg-slate-100"><Filter className="w-4 h-4 text-slate-500" /></button>
          </div>
        </div>
        <div className="max-w-4xl mx-auto mt-2">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by event, client, role, venue…"
            className="w-full text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            data-testid="bookings-search"
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
        {loading ? (
          <div className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
        ) : groups.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="bookings-empty">
            <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <h3 className="font-semibold text-slate-700">No bookings yet</h3>
            <p className="text-sm text-slate-500 mt-1">Apply to gigs to start building your schedule.</p>
            <Button onClick={() => navigate('/talent/gigs')} className="mt-4 bg-cyan-600 hover:bg-cyan-700">Browse gigs</Button>
          </div>
        ) : (
          groups.map(g => (
            <ConsolidatedCard
              key={g.key}
              group={g}
              expanded={expanded.has(g.key)}
              onToggle={toggle}
              onOpenActions={openActions}
              onShiftClick={onShiftClick}
            />
          ))
        )}
      </div>

      <BookingActionsSheet
        open={!!actionTarget}
        onClose={() => setActionTarget(null)}
        group={actionTarget?.group}
        shift={actionTarget?.shift}
      />
    </div>
  );
};

export default BookingsV2;
